import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import ECharts from "@/components/Chart/ECharts";
import DateRangeSelector from "./_DateRangeSelector";
import DropdownCheckbox from "@/components/Dropdown/DropdownCheckboxKpi";
import { IDropdown } from "@/constants/default";
import {
    useGetBatchCompanyOperationalKpisDataQuery,
    useGetBatchFinancialKpisDataQuery,
    useGetBatchGrowthKpisDataQuery,
    useGetBatchOperationalKpisDataQuery,
    useGetBatchRoomFinancialKpisDataQuery,
    useGetBatchRoomOperationalKpisDataQuery,
    useGetBatchUtilitiesKpisDataQuery,
    useGetExpenseBreakdownQuery,
    useGetKpiUnitRoomsQuery,
    useGetPropertiesQuery,
} from "@/services/kpi.service";
import { FinancialKpisRes, GrowthKpisRes, BatchFinancialKpiResponse, BatchFinancialKpiItem, OperationalKpisRes, UtilitiesKpisRes, KpiRoomInfo, QuarterlyRevenueData } from "@/types/kpi.type";
import { MonthlyMaintenanceDataPoint, PropertyInfo } from "@/types/kpi.type";

export { CashflowNestedDrilldown } from "./_CashflowDrilldown";



interface MaintenanceDrilldownProps {
    monthlyMaintenance: MonthlyMaintenanceDataPoint[];
    maintenanceUnits: PropertyInfo[];
    inactiveUnitIds?: Set<string>;
    helperNote?: string;
}

type HistoricalMetricKey = "gross_margin" | "net_margin" | "cashflow" | "opex_ratio" | "revenue_growth_rate";
interface HistoricalSummaryDrilldownProps {
    title: string;
    metric: HistoricalMetricKey;
    format: "percent" | "currency" | "days" | "number";
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
    rowLabel?: string;
    helperNote?: string;
}

interface RevenueNestedDrilldownProps {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
}

interface OccupancyNestedDrilldownProps {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
}

interface UtilityExpenditureDrilldownProps {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
}



type HistoricalColumn = {
    key: string;
    label: string;
    range: string;
};

type MonthlyChartColumn = {
    key: string;
    label: string;
    start: string;
    end: string;
};

type GrossMarginRow = {
    unitId: string;
    label: string;
    values: Record<string, number | null>;
};

type BreakevenRow = {
    unitId: string;
    label: string;
    breakevenMonths: number | null;
    isActive: boolean;
    projectedBreakevenMonths: number | null;
    projectedBreakevenDate: string | null;
    lastMonthCashflow: number | null;
};

const DEFAULT_EXCLUDED_COMPARISON_UNITS = new Set(["SCKYROOM", "BANK"]);

const sortComparisonUnits = (units: IDropdown[]) =>
    [...units].sort((a, b) => {
        const aIsSpecial = DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(a.label).trim().toUpperCase());
        const bIsSpecial = DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(b.label).trim().toUpperCase());

        if (aIsSpecial === bIsSpecial) return 0;
        return aIsSpecial ? 1 : -1;
    });

const formatValue = (
    value: number | null | undefined,
    format: "percent" | "currency" | "days" | "number"
) => {
    if (value == null || Number.isNaN(value)) return "N/A";

    switch (format) {
        case "percent":
            return `${value.toFixed(1)}%`;
        case "currency":
            return value.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                currencyDisplay: "symbol",
                maximumFractionDigits: 0,
            });
        case "days":
            return `${value.toFixed(1)} days`;
        default:
            return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
    }
};

const formatMonthlyValue = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return "N/A";
    return `${formatValue(value, "currency")} / mth`;
};

const splitMonthLabel = (label: string) => {
    const parts = label.split(" ");
    if (parts.length >= 2) {
        return {
            month: parts[0],
            year: parts.slice(1).join(" "),
        };
    }

    return {
        month: label,
        year: "",
    };
};

const getHistoricalColumns = (): HistoricalColumn[] => {
    const currentYear = new Date().getFullYear();

    return [
        { key: "last_12_months", label: "Last 12 Months", range: "last_12_months" },
        ...Array.from({ length: 5 }, (_, index) => {
            const year = currentYear - 1 - index;
            return {
                key: `year_${year}`,
                label: String(year),
                range: `year_${year}`,
            };
        }),
    ];
};

const getLatestFiveCalendarYearColumns = (): HistoricalColumn[] => {
    const currentYear = new Date().getFullYear();

    return [
        { key: `year_${currentYear}`, label: String(currentYear), range: "ytd" },
        ...Array.from({ length: 4 }, (_, index) => {
            const year = currentYear - 1 - index;
            return {
                key: `year_${year}`,
                label: String(year),
                range: `year_${year}`,
            };
        }),
    ];
};

const getLatestFiveCalendarYearColumnsCompletedMonth = (): HistoricalColumn[] => {
    const currentYear = new Date().getFullYear();

    return [
        { key: `year_${currentYear}`, label: String(currentYear), range: "current_year_through_last_completed_month" },
        ...Array.from({ length: 4 }, (_, index) => {
            const year = currentYear - 1 - index;
            return {
                key: `year_${year}`,
                label: String(year),
                range: `year_${year}`,
            };
        }),
    ];
};

/**
 * Generates columns for Revenue Growth KPI using "last completed quarter" logic.
 * First column shows current year through the last completed quarter with label like "2026 (Q1-Q3)".
 * If no quarter is complete in current year (Jan-Mar), shows previous year with "(Q1-Q4)".
 */
const getRevenueGrowthColumns = (): HistoricalColumn[] => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed

    // Determine last completed quarter
    let lastCompletedQuarter: number;
    let labelYear: number;

    if (currentMonth >= 9) {
        // Oct-Dec (Q4): Q3 is complete
        lastCompletedQuarter = 3;
        labelYear = currentYear;
    } else if (currentMonth >= 6) {
        // Jul-Sep (Q3): Q2 is complete
        lastCompletedQuarter = 2;
        labelYear = currentYear;
    } else if (currentMonth >= 3) {
        // Apr-Jun (Q2): Q1 is complete
        lastCompletedQuarter = 1;
        labelYear = currentYear;
    } else {
        // Jan-Mar (Q1): Q4 of previous year is complete
        lastCompletedQuarter = 4;
        labelYear = currentYear - 1;
    }

    // Label format: "2026 (Q1-Q3)" or "2025 (Q1-Q4)" for full year
    const quarterLabel = lastCompletedQuarter === 1 ? "(Q1)" : `(Q1-Q${lastCompletedQuarter})`;

    return [
        {
            key: `year_${labelYear}_q${lastCompletedQuarter}`,
            label: `${labelYear} ${quarterLabel}`,
            range: "current_year_through_last_completed_quarter",
        },
        ...Array.from({ length: 4 }, (_, index) => {
            const year = labelYear - 1 - index;
            return {
                key: `year_${year}`,
                label: String(year),
                range: `year_${year}`,
            };
        }),
    ];
};

const getMaintenanceColumns = (): HistoricalColumn[] => {
    const currentYear = new Date().getFullYear();

    return [
        { key: `year_${currentYear}`, label: String(currentYear), range: "current_year_through_last_completed_month" },
        ...Array.from({ length: 4 }, (_, index) => {
            const year = currentYear - 1 - index;
            return {
                key: `year_${year}`,
                label: String(year),
                range: `year_${year}`,
            };
        }),
    ];
};

const UTILITY_CHART_COLORS = ["#fac858", "#ee6666", "#5470c6", "#91cc75", "#73c0de", "#3ba272"];
const REVENUE_CHART_COLORS = ["#5470c6", "#91cc75", "#fac858", "#ee6666", "#73c0de", "#3ba272", "#fc8452", "#9a60b4", "#ea7ccc", "#2f4554", "#61a0a8", "#d48265"];

const formatDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getLast12CompletedMonthColumns = (): MonthlyChartColumn[] => {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const formatter = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });

    return Array.from({ length: 12 }, (_, index) => {
        const monthStart = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth() - (12 - index), 1);
        const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
        const start = formatDateInput(monthStart);
        const end = formatDateInput(monthEnd);

        return {
            key: `${start}_${end}`,
            label: formatter.format(monthStart),
            start,
            end,
        };
    });
};

const getFinancialMetricValue = (data: FinancialKpisRes, metric: HistoricalMetricKey) => {
    switch (metric) {
        case "gross_margin":
            return data.gross_margin;
        case "net_margin":
            return data.net_margin;
        case "cashflow":
            return data.cashflow;
        case "opex_ratio":
            return data.opex_ratio;
        default:
            return null;
    }
};

const getGrowthMetricValue = (data: GrowthKpisRes, metric: HistoricalMetricKey) => {
    if (metric === "revenue_growth_rate") {
        return data.revenue_growth_rate;
    }
    return null;
};

const sortRooms = (rooms: KpiRoomInfo[]) =>
    [...rooms].sort((a, b) =>
        String(a.code).localeCompare(String(b.code), undefined, {
            numeric: true,
            sensitivity: "base",
        })
    );

// DISABLED: Not currently used, kept for potential future use
/* export function HistoricalSummaryDrilldown({
    title,
    metric,
    format,
    businessUnits,
    rowLabel = "Unit",
    helperNote,
}: HistoricalSummaryDrilldownProps) {
    const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
    const columns = useMemo(() => getHistoricalColumns(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnitIds = useMemo(
        () =>
            comparisonUnits
                .filter((unit) => !DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(unit.label).trim().toUpperCase()))
                .map((unit) => String(unit.value)),
        [comparisonUnits]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnitIds([]);
            return;
        }

        setSelectedUnitIds((current) => {
            const validCurrent = current.filter((id) => comparisonUnits.some((unit) => unit.value === id));
            if (validCurrent.length > 0) return validCurrent;
            return defaultSelectedUnitIds;
        });
    }, [comparisonUnits, defaultSelectedUnitIds]);
    // Fetch ALL available units (not just selected) to enable local filtering
    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );
    const financialBatchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
            level: "L2",
        }),
        [columns, allUnitIds]
    );
    const growthBatchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
        }),
        [columns, allUnitIds]
    );
    const {
        data: financialBatchResult,
        isFetching: financialLoading,
        error: financialError,
    } = useGetBatchFinancialKpisDataQuery(financialBatchArgs, {
        skip: metric === "revenue_growth_rate" || allUnitIds.length === 0,
    });
    const {
        data: growthBatchResult,
        isFetching: growthLoading,
        error: growthError,
    } = useGetBatchGrowthKpisDataQuery(growthBatchArgs, {
        skip: metric !== "revenue_growth_rate" || allUnitIds.length === 0,
    });
    const loading = metric === "revenue_growth_rate" ? growthLoading : financialLoading;
    const error = metric === "revenue_growth_rate" ? growthError : financialError;
    // Filter rows locally based on selectedUnitIds
    const rows = useMemo(
        () => {
            const selectedIdSet = new Set(selectedUnitIds);
            return comparisonUnits
                .filter(unit => selectedIdSet.has(String(unit.value)))
                .map((unit) => {
                    const unitId = String(unit.value);
                    const label = unit.label;
                    const values = Object.fromEntries(
                        columns.map((column) => {
                            const key = `${unitId}_${column.range}`;
                            const data =
                                metric === "revenue_growth_rate"
                                    ? growthBatchResult?.results[key]
                                    : financialBatchResult?.results[key];
                            const value =
                                metric === "revenue_growth_rate"
                                    ? data && "revenue_growth_rate" in data
                                        ? getGrowthMetricValue(data as GrowthKpisRes, metric)
                                    : null
                                : data && "gross_margin" in data
                                  ? getFinancialMetricValue(data as FinancialKpisRes, metric)
                                  : null;
                            return [column.key, value];
                        })
                    );
                    return { unitId, label, values };
                });
        },
        [columns, comparisonUnits, financialBatchResult?.results, growthBatchResult?.results, metric, selectedUnitIds]
    );

    const handleSelectAll = () => {
        setSelectedUnitIds(defaultSelectedUnitIds);
    };

    const handleClear = () => {
        setSelectedUnitIds([]);
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare the selected units across the latest 12 months and the last 5 full years.
                </p>
                {helperNote && (
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                        {helperNote}
                    </div>
                )}
            </div>

            {loading && <p className="text-sm text-gray-500">Loading KPI history...</p>}
            {!loading && error && <p className="text-sm text-red-500">Unable to load historical KPI data.</p>}
            {!loading && !error && selectedUnitIds.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!loading && !error && selectedUnitIds.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {rowLabel}
                                </th>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.unitId} className="border-t border-stroke dark:border-strokedark">
                                    <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                                        {row.label}
                                    </td>
                                    {columns.map((column) => (
                                        <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                            {formatValue(row.values[column.key], format)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
} */

export function GrossMarginDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
    rowLabel = "Unit",
    helperNote,
}: {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
    rowLabel?: string;
    helperNote?: string;
}) {
    const STORAGE_KEY = "kpi_gross_margin_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const columns = useMemo(() => getLatestFiveCalendarYearColumnsCompletedMonth(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () =>
            comparisonUnits
                .filter((unit) => !DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(unit.label).trim().toUpperCase()))
                .filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default
        setSelectedUnits((current) => {
            const validCurrent = current.filter((unit) =>
                comparisonUnits.some((u) => String(u.value) === String(unit.value))
            );
            if (validCurrent.length > 0) return validCurrent;
            return defaultSelectedUnits;
        });
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);
    // Fetch ALL available units (not just selected) to enable local filtering
    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );
    const batchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
            level: "L2",
            metrics: ["gross_margin"],
        }),
        [columns, allUnitIds]
    );
    const { data: batchResult, isFetching: loading, error } = useGetBatchFinancialKpisDataQuery(batchArgs, {
        skip: allUnitIds.length === 0,
    });
    // Filter rows locally based on selectedUnits
    const rows = useMemo<GrossMarginRow[]>(
        () => {
            const selectedUnitIds = new Set(selectedUnits.map(u => String(u.value)));
            return comparisonUnits
                .filter(unit => selectedUnitIds.has(String(unit.value)))
                .map((unit) => {
                    const unitId = String(unit.value);
                    const values = Object.fromEntries(
                        columns.map((column) => {
                            const data = batchResult?.results[`${unitId}_${column.range}`];
                            return [column.key, data?.gross_margin ?? null];
                        })
                    );

                    return {
                        unitId,
                        label: unit.label,
                        values,
                    };
                });
        },
        [batchResult?.results, columns, comparisonUnits, selectedUnits]
    );

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare the selected units across the current year through the end of the last completed month and the previous 4 full calendar years.
                </p>
                {helperNote && (
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                        {helperNote}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {loading && <p className="text-sm text-gray-500">Loading gross margin history...</p>}
            {!loading && error && <p className="text-sm text-red-500">Unable to load gross margin history.</p>}
            {!loading && !error && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {rowLabel}
                                </th>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.unitId} className="border-t border-stroke dark:border-strokedark">
                                    <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                                        {row.label}
                                    </td>
                                    {columns.map((column) => (
                                        <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                            {formatValue(row.values[column.key], "percent")}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export function NetMarginDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
    rowLabel = "Unit",
    helperNote,
}: {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
    rowLabel?: string;
    helperNote?: string;
}) {
    const STORAGE_KEY = "kpi_net_margin_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const columns = useMemo(() => getLatestFiveCalendarYearColumnsCompletedMonth(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () =>
            comparisonUnits
                .filter((unit) => !DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(unit.label).trim().toUpperCase()))
                .filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default
        setSelectedUnits((current) => {
            const validCurrent = current.filter((unit) =>
                comparisonUnits.some((u) => String(u.value) === String(unit.value))
            );
            if (validCurrent.length > 0) return validCurrent;
            return defaultSelectedUnits;
        });
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);
    // Fetch ALL available units (not just selected) to enable local filtering
    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );
    const batchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
            level: "L2",
            metrics: ["net_margin"],
        }),
        [columns, allUnitIds]
    );
    const { data: batchResult, isFetching: loading, error } = useGetBatchFinancialKpisDataQuery(batchArgs, {
        skip: allUnitIds.length === 0,
    });
    // Filter rows locally based on selectedUnits
    const rows = useMemo<GrossMarginRow[]>(
        () => {
            const selectedUnitIds = new Set(selectedUnits.map(u => String(u.value)));
            return comparisonUnits
                .filter(unit => selectedUnitIds.has(String(unit.value)))
                .map((unit) => {
                    const unitId = String(unit.value);
                    const values = Object.fromEntries(
                        columns.map((column) => {
                            const data = batchResult?.results[`${unitId}_${column.range}`];
                            return [column.key, data?.net_margin ?? null];
                        })
                    );

                    return {
                        unitId,
                        label: unit.label,
                        values,
                };
            });
        },
        [batchResult?.results, columns, comparisonUnits, selectedUnits]
    );

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare the selected units across the current year through the end of the last completed month and the previous 4 full calendar years.
                </p>
                {helperNote && (
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                        {helperNote}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {loading && <p className="text-sm text-gray-500">Loading net margin history...</p>}
            {!loading && error && <p className="text-sm text-red-500">Unable to load net margin history.</p>}
            {!loading && !error && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {rowLabel}
                                </th>
                                {columns.map((column) => (
                                    <th key={column.key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.unitId} className="border-t border-stroke dark:border-strokedark">
                                    <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                                        {row.label}
                                    </td>
                                    {columns.map((column) => (
                                        <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                            {formatValue(row.values[column.key], "percent")}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export function OpexRatioDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
    rowLabel = "Unit",
    helperNote,
    metric = "opex_ratio",
    format = "percent",
}: {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
    rowLabel?: string;
    helperNote?: string;
    metric?: "opex_ratio" | "cashflow";
    format?: "percent" | "currency";
}) {
    const STORAGE_KEY = metric === "cashflow" ? "kpi_cashflow_selected_units" : "kpi_opex_ratio_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const columns = useMemo(() => getLatestFiveCalendarYearColumnsCompletedMonth(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () =>
            comparisonUnits
                .filter((unit) => !DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(unit.label).trim().toUpperCase()))
                .filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default
        setSelectedUnits((current) => {
            const validCurrent = current.filter((unit) =>
                comparisonUnits.some((u) => String(u.value) === String(unit.value))
            );
            if (validCurrent.length > 0) return validCurrent;
            return defaultSelectedUnits;
        });
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);
    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );
    const batchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
            level: "L2",
            metrics: metric === "cashflow" ? ["cashflow"] : ["opex_ratio"],
        }),
        [columns, allUnitIds, metric]
    );
    const { data: batchResult, isFetching: loading, error } = useGetBatchFinancialKpisDataQuery(batchArgs, {
        skip: allUnitIds.length === 0,
    });
    const rows = useMemo(() => {
        const selectedUnitIds = new Set(selectedUnits.map(u => String(u.value)));
        return comparisonUnits
            .filter(unit => selectedUnitIds.has(String(unit.value)))
            .map((unit) => {
                const unitId = String(unit.value);
                const values = Object.fromEntries(
                    columns.map((column) => {
                        const data = batchResult?.results[`${unitId}_${column.range}`];
                        const value = metric === "cashflow" ? data?.cashflow ?? null : data?.opex_ratio ?? null;
                        return [column.key, value];
                    })
                );

                return { unitId, label: unit.label, values };
            });
        },
        [batchResult?.results, columns, comparisonUnits, metric, selectedUnits]
    );

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare the selected units across the current year through the end of the last completed month and the previous 4 full calendar years.
                </p>
                {helperNote && (
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                        {helperNote}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {loading && (
                <p className="text-sm text-gray-500">
                    {metric === "cashflow" ? "Loading cashflow history..." : "Loading operating expense ratio history..."}
            </p>
            )}
            {!loading && error && <p className="text-sm text-red-500">{metric === "cashflow" ? "Unable to load cashflow history." : "Unable to load operating expense ratio history."}</p>}
            {!loading && !error && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {rowLabel}
                                </th>
                                {columns.map((column) => (
                                    <th key={column.key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.unitId} className="border-t border-stroke dark:border-strokedark">
                                    <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                                        {row.label}
                                    </td>
                                    {columns.map((column) => (
                                    <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                            {formatValue(row.values[column.key], format)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export function BreakevenDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
    rowLabel = "Unit",
    helperNote,
}: {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
    rowLabel?: string;
    helperNote?: string;
}) {
    const STORAGE_KEY = "kpi_breakeven_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () =>
            comparisonUnits
                .filter((unit) => !DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(unit.label).trim().toUpperCase()))
                .filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) => storedIds.includes(String(unit.value)));
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        setSelectedUnits((current) => {
            const validCurrent = current.filter((unit) =>
                comparisonUnits.some((candidate) => String(candidate.value) === String(unit.value))
            );
            if (validCurrent.length > 0) return validCurrent;
            return defaultSelectedUnits;
        });
    }, [comparisonUnits, defaultSelectedUnits]);

    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((unit) => String(unit.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);

    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );
    const batchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: ["current_year_through_last_completed_month"],
            level: "L2",
            metrics: ["breakeven_months"],
        }),
        [allUnitIds]
    );
    const { data: batchResult, isFetching: loading, error } = useGetBatchFinancialKpisDataQuery(batchArgs, {
        skip: allUnitIds.length === 0,
    });
    const rows = useMemo<BreakevenRow[]>(
        () => {
            const selectedUnitIds = new Set(selectedUnits.map((unit) => String(unit.value)));
            return comparisonUnits
                .filter((unit) => selectedUnitIds.has(String(unit.value)))
                .map((unit) => {
                    const unitId = String(unit.value);
                    const data = batchResult?.results[`${unitId}_current_year_through_last_completed_month`];
                    return {
                        unitId,
                        label: unit.label,
                        breakevenMonths: data?.breakeven_months ?? null,
                        isActive: data?.is_breakeven_active ?? true,
                        projectedBreakevenMonths: data?.projected_breakeven_months ?? null,
                        projectedBreakevenDate: data?.projected_breakeven_date ?? null,
                        lastMonthCashflow: data?.last_month_cashflow ?? null,
                    };
                });
        },
        [batchResult?.results, comparisonUnits, selectedUnits]
    );

    const renderBreakevenValue = (row: BreakevenRow): React.ReactNode => {
        // Case 1: Already broken even
        if (row.breakevenMonths != null) {
            return `${row.breakevenMonths} ${row.breakevenMonths === 1 ? "month" : "months"}`;
        }

        // Case 2: Not broken even AND inactive - show infinity
        if (!row.isActive) {
            return (
                <span className="text-gray-500 dark:text-gray-400" title="Lease ended without breaking even">
                    ∞
                </span>
            );
        }

        // Case 3: Active, not broken even, positive cashflow with projection
        if (row.projectedBreakevenMonths != null && row.projectedBreakevenDate) {
            return (
                <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Projected breakeven in {row.projectedBreakevenMonths}{" "}
                        {row.projectedBreakevenMonths === 1 ? "month" : "months"} based on latest month's
                        cashflow ({row.projectedBreakevenDate})
                    </p>
                </div>
            );
        }

        // Case 4: Active, not broken even, zero/negative cashflow - show infinity
        return (
            <span className="text-gray-500 dark:text-gray-400" title="Negative cashflow - cannot project">
                ∞
            </span>
        );
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare when each selected unit first recovers initial CAPEX plus pre-TA-start COGS using first-lease cumulative cashflow only.
                </p>
                {helperNote && (
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                        {helperNote}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setSelectedUnits(defaultSelectedUnits)}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {loading && <p className="text-sm text-gray-500">Loading breakeven history...</p>}
            {!loading && error && <p className="text-sm text-red-500">Unable to load breakeven history.</p>}
            {!loading && !error && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-fixed">
                        <colgroup>
                            <col className="w-40 sm:w-48" />
                            <col />
                        </colgroup>
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {rowLabel}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Breakeven
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.unitId} className="border-t border-stroke dark:border-strokedark">
                                    <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                                        <div className="truncate" title={!row.isActive ? `${row.label} (Inactive)` : row.label}>
                                            {row.label}
                                            {!row.isActive && (
                                                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                                                    (Inactive)
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-black dark:text-white">
                                        {renderBreakevenValue(row)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Helper to determine which 2 years to show for quarterly breakdown.
// If Q1 of current year is complete (we're in April or later), show current and previous year.
// Otherwise show last year and the year before that.
function getQuarterlyYears(): [number, number] {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed

    // Q1 is complete if we're in April (month 3) or later
    if (currentMonth >= 3) {
        return [currentYear, currentYear - 1];
    }
    return [currentYear - 1, currentYear - 2];
}

type RevenueGrowthRow = {
    unitId: string;
    label: string;
    values: Record<string, number | null>;
    quarterlyRevenue: Record<string, QuarterlyRevenueData>;
};

export function RevenueGrowthDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
    rowLabel = "Unit",
    helperNote,
}: {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
    rowLabel?: string;
    helperNote?: string;
}) {
    const STORAGE_KEY = "kpi_revenue_growth_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
    const columns = useMemo(() => getRevenueGrowthColumns(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () =>
            comparisonUnits
                .filter((unit) => !DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(unit.label).trim().toUpperCase()))
                .filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    // Get the 2 years to show for quarterly breakdown
    const [year1, year2] = useMemo(() => getQuarterlyYears(), []);

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default
        setSelectedUnits((current) => {
            const validCurrent = current.filter((unit) =>
                comparisonUnits.some((u) => String(u.value) === String(unit.value))
            );
            if (validCurrent.length > 0) return validCurrent;
            return defaultSelectedUnits;
        });
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);

    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );

    // Fetch with include_quarterly=true for expanded quarterly data
    const batchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
            include_quarterly: true,
        }),
        [columns, allUnitIds]
    );

    const { data: batchResult, isFetching: loading, error } = useGetBatchGrowthKpisDataQuery(batchArgs, {
        skip: allUnitIds.length === 0,
    });

    const rows = useMemo<RevenueGrowthRow[]>(() => {
        const selectedUnitIds = new Set(selectedUnits.map(u => String(u.value)));
        return comparisonUnits
            .filter(unit => selectedUnitIds.has(String(unit.value)))
            .map((unit) => {
                const unitId = String(unit.value);
                const values = Object.fromEntries(
                    columns.map((column) => {
                        const key = `${unitId}_${column.range}`;
                        return [column.key, batchResult?.results[key]?.current_revenue ?? null];
                    })
                );
                // Get quarterly data from the first range result.
                const firstRangeKey = `${unitId}_${columns[0]?.range}`;
                const quarterlyRevenue = batchResult?.results[firstRangeKey]?.quarterly_revenue ?? {};

                return {
                    unitId,
                    label: unit.label,
                    values,
                    quarterlyRevenue,
                };
            });
        },
        [batchResult?.results, columns, comparisonUnits, selectedUnits]
    );

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const toggleUnitExpansion = (unitId: string) => {
        setExpandedUnitIds((current) => {
            const next = new Set(current);
            if (next.has(unitId)) {
                next.delete(unitId);
            } else {
                next.add(unitId);
            }
            return next;
        });
    };

    // Get quarter value from quarterly data
    const getQuarterValue = (quarterlyRevenue: Record<string, QuarterlyRevenueData>, year: number, quarter: 1 | 2 | 3 | 4): number | null | undefined => {
        const yearData = quarterlyRevenue[String(year)];
        if (!yearData) return null;
        switch (quarter) {
            case 1: return yearData.q1;
            case 2: return yearData.q2;
            case 3: return yearData.q3;
            case 4: return yearData.q4;
        }
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    View revenue totals for the selected units. Click a row to see Q1-Q4 revenue breakdown.
                </p>
                {helperNote && (
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                        {helperNote}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {loading && <p className="text-sm text-gray-500">Loading revenue history...</p>}
            {!loading && error && <p className="text-sm text-red-500">Unable to load revenue history.</p>}
            {!loading && !error && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to view.</p>
            )}

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {rowLabel}
                                </th>
                                {columns.map((column) => (
                                    <th key={column.key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const isExpanded = expandedUnitIds.has(row.unitId);

                                return (
                                    <Fragment key={row.unitId}>
                                        <tr
                                            className="cursor-pointer border-t border-stroke hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4/20"
                                            onClick={() => toggleUnitExpansion(row.unitId)}
                                        >
                                            <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                                                <div className="flex items-center gap-3">
                                                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stroke text-xs font-semibold text-bodydark2 dark:border-strokedark">
                                                        {isExpanded ? "−" : "+"}
                                                    </span>
                                                    <span>{row.label}</span>
                                                </div>
                                            </td>
                                            {columns.map((column) => (
                                                <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                                    {formatValue(row.values[column.key], "currency")}
                                                </td>
                                            ))}
                                        </tr>

                                        {/* Expanded quarterly revenue breakdown - Q1 to Q4 rows aligned with year columns */}
                                        {isExpanded && ([1, 2, 3, 4] as const).map((quarter) => (
                                            <tr key={`${row.unitId}_q${quarter}`} className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                                                <td className="px-4 py-2 pl-14 text-sm text-gray-600 dark:text-gray-400">
                                                    Q{quarter}
                                                </td>
                                                {columns.map((column) => {
                                                    // Extract year from column label (e.g., "2026")
                                                    const year = parseInt(column.label, 10);
                                                    const hasQuarterlyData = year === year1 || year === year2;
                                                    const quarterValue = hasQuarterlyData
                                                        ? getQuarterValue(row.quarterlyRevenue, year, quarter)
                                                        : undefined;

                                                    return (
                                                        <td key={column.key} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                                                            {hasQuarterlyData && quarterValue != null ? formatValue(quarterValue, "currency") : "—"}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export function FormulaAside({
    formula,
    notes = [],
}: {
    formula: string;
    notes?: string[];
}) {
    return (
        <div className="rounded-sm border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Formula</p>
            <p className="mt-2 text-sm font-medium text-black dark:text-white">{formula}</p>
            {notes.length > 0 && (
                <div className="mt-3 space-y-2 border-t border-stroke pt-3 dark:border-strokedark">
                    {notes.map((note) => (
                        <p key={note} className="text-xs leading-5 text-gray-500 dark:text-gray-400">
                            {note}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
}

// Component for rendering a single unit row with independent room data fetching
function RevenueUnitRow({
    row,
    columns,
    isExpanded,
    onToggle,
    formatValue,
    formatMonthlyValue,
}: {
    row: { unitId: string; label: string; revpauValues: Record<string, number | null>; arpuValues: Record<string, number | null> };
    columns: Array<{ key: string; label: string; range: string }>;
    isExpanded: boolean;
    onToggle: () => void;
    formatValue: (value: number | null | undefined, format: "percent" | "currency" | "days" | "number") => string;
    formatMonthlyValue: (value: number | null) => string;
}) {
    // Fetch rooms for this specific unit when expanded
    const { data: unitRoomsResponse } = useGetKpiUnitRoomsQuery(row.unitId, {
        skip: !isExpanded,
    });

    const expandedRooms = useMemo(
        () => sortRooms((unitRoomsResponse?.rooms || []).filter((room) => room.id)),
        [unitRoomsResponse?.rooms]
    );

    const roomBatchArgs = useMemo(
        () => ({
            business_unit_id: row.unitId,
            room_ids: expandedRooms.map((room) => String(room.id)).sort((a, b) => a.localeCompare(b)),
            ranges: columns.map((column) => column.range),
            metrics: ["revpar", "arpr"],
        }),
        [columns, expandedRooms, row.unitId]
    );

    const {
        data: roomBatchResult,
        isFetching: isRoomLoading,
        error: roomError,
    } = useGetBatchRoomFinancialKpisDataQuery(roomBatchArgs, {
        skip: !isExpanded || expandedRooms.length === 0,
    });

    const roomRows = useMemo(
        () =>
            expandedRooms.map((room) => {
                const roomId = String(room.id);
                const revparValues = Object.fromEntries(
                    columns.map((column) => {
                        const key = `${roomId}_${column.range}`;
                        const data = roomBatchResult?.results[key];
                        return [column.key, data?.revpar ?? null];
                    })
                );
                const arprValues = Object.fromEntries(
                    columns.map((column) => {
                        const key = `${roomId}_${column.range}`;
                        const data = roomBatchResult?.results[key];
                        return [column.key, data?.arpr ?? null];
                    })
                );
                return {
                    roomId,
                    label: room.code,
                    revparValues,
                    arprValues,
                };
            }),
        [columns, expandedRooms, roomBatchResult?.results]
    );

    return (
        <Fragment key={row.unitId}>
            <tr
                key={`${row.unitId}:revpau`}
                className="cursor-pointer border-t border-stroke hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4/20"
                onClick={onToggle}
            >
                <td rowSpan={2} className="px-4 py-3 align-top text-sm font-medium text-black dark:text-white">
                    <div className="flex items-center gap-3 text-left">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stroke text-xs font-semibold text-bodydark2 dark:border-strokedark">
                            {isExpanded ? "-" : "+"}
                        </span>
                        <span>{row.label}</span>
                    </div>
                </td>
                {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                        {formatValue(row.revpauValues[column.key], "currency")}
                    </td>
                ))}
            </tr>
            <tr
                key={`${row.unitId}:arpu`}
                className="cursor-pointer border-t border-stroke bg-gray-50/40 hover:bg-gray-50 dark:border-strokedark dark:bg-meta-4/10 dark:hover:bg-meta-4/20"
                onClick={onToggle}
            >
                {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                        {formatMonthlyValue(row.arpuValues[column.key])}
                    </td>
                ))}
            </tr>
            {isExpanded && isRoomLoading && (
                <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                    <td colSpan={columns.length + 1} className="px-4 py-3 text-sm text-gray-500">
                        Loading revenue by room...
                    </td>
                </tr>
            )}
            {isExpanded && !isRoomLoading && roomError && (
                <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                    <td colSpan={columns.length + 1} className="px-4 py-3 text-sm text-red-500">
                        Unable to load revenue by room.
                    </td>
                </tr>
            )}
            {isExpanded &&
                !isRoomLoading &&
                !roomError &&
                roomRows.map((roomRow) => (
                    <Fragment key={`${row.unitId}:${roomRow.roomId}`}>
                        <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                            <td rowSpan={2} className="px-4 py-3 pl-14 align-top text-sm text-black dark:text-white">
                                {roomRow.label}
                            </td>
                            {columns.map((column) => (
                                <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                    {formatValue(roomRow.revparValues[column.key], "currency")}
                                </td>
                            ))}
                        </tr>
                        <tr className="border-t border-stroke bg-gray-50/70 dark:border-strokedark dark:bg-meta-4/20">
                            {columns.map((column) => (
                                <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                    {formatMonthlyValue(roomRow.arprValues[column.key])}
                                </td>
                            ))}
                        </tr>
                    </Fragment>
                ))}
        </Fragment>
    );
}

export function RevenueNestedDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
}: RevenueNestedDrilldownProps) {
    const STORAGE_KEY = "kpi_revenue_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
    const columns = useMemo(() => getLatestFiveCalendarYearColumnsCompletedMonth(), []);
    const monthColumns = useMemo(() => getLast12CompletedMonthColumns(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () => comparisonUnits.filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default (all units)
        setSelectedUnits(defaultSelectedUnits);
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);
    // Fetch ALL available units (not just selected) to enable local filtering
    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );
    const unitBatchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
            level: "L2",
            metrics: ["revpau", "arpu"],
        }),
        [columns, allUnitIds]
    );
    const chartBatchArgs = useMemo(
        () => ({
            level: "L2",
            metrics: ["total_revenue"],
            requests: allUnitIds.flatMap((unitId) =>
                monthColumns.map<BatchFinancialKpiItem>((column) => ({
                    key: `${unitId}_${column.key}`,
                    range: "custom",
                    start: column.start,
                    end: column.end,
                    business_unit_id: unitId,
                    level: "L2",
                }))
            ),
        }),
        [allUnitIds, monthColumns]
    );
    const {
        data: unitBatchResult,
        isFetching: loading,
        error,
    } = useGetBatchFinancialKpisDataQuery(unitBatchArgs, {
        skip: allUnitIds.length === 0,
    });
    const {
        data: chartBatchResult,
        isFetching: chartLoading,
        error: chartError,
    } = useGetBatchFinancialKpisDataQuery(chartBatchArgs, {
        skip: allUnitIds.length === 0,
    });
    // Filter rows locally based on selectedUnits
    const rows = useMemo(
        () => {
            const selectedUnitIds = new Set(selectedUnits.map(u => String(u.value)));
            return comparisonUnits
                .filter(unit => selectedUnitIds.has(String(unit.value)))
                .map((unit) => {
                    const unitId = String(unit.value);
                    const revpauValues = Object.fromEntries(
                        columns.map((column) => {
                            const key = `${unitId}_${column.range}`;
                            const data = unitBatchResult?.results[key];
                            return [column.key, data?.revpau ?? null];
                        })
                    );
                    const arpuValues = Object.fromEntries(
                        columns.map((column) => {
                            const key = `${unitId}_${column.range}`;
                            const data = unitBatchResult?.results[key];
                            return [column.key, data?.arpu ?? null];
                        })
                    );
                    return { unitId, label: unit.label, revpauValues, arpuValues };
                });
        },
        [columns, comparisonUnits, selectedUnits, unitBatchResult?.results]
    );
    const revenueChart = useMemo(() => {
        const selectedUnitIds = new Set(selectedUnits.map((unit) => String(unit.value)));
        const visibleUnits = comparisonUnits.filter((unit) => selectedUnitIds.has(String(unit.value)));
        const colorByUnitId = Object.fromEntries(
            comparisonUnits.map((unit, index) => [String(unit.value), REVENUE_CHART_COLORS[index % REVENUE_CHART_COLORS.length]])
        );

        const series = visibleUnits.map((unit) => {
            const unitId = String(unit.value);
            return {
                name: unit.label,
                type: "bar",
                stack: "revenue",
                data: monthColumns.map((column) => chartBatchResult?.results[`${unitId}_${column.key}`]?.total_revenue ?? 0),
                itemStyle: {
                    color: colorByUnitId[unitId],
                },
            };
        });

        const monthlyTotals = monthColumns.map((column) =>
            visibleUnits.reduce((sum, unit) => sum + (chartBatchResult?.results[`${String(unit.value)}_${column.key}`]?.total_revenue ?? 0), 0)
        );
        const average = monthlyTotals.length > 0
            ? monthlyTotals.reduce((sum, value) => sum + value, 0) / monthlyTotals.length
            : 0;

        return {
            labels: monthColumns.map((column) => column.label),
            visibleUnits,
            colorByUnitId,
            series: [
                ...series,
                {
                    name: "Average",
                    type: "line",
                    data: monthColumns.map(() => average),
                    symbol: "none",
                    lineStyle: {
                        type: "dashed",
                        width: 2,
                    },
                    itemStyle: {
                        color: "#2f4554",
                    },
                    tooltip: {
                        valueFormatter: (value: number) =>
                            value.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                                currencyDisplay: "symbol",
                                maximumFractionDigits: 0,
                            }),
                    },
                },
            ],
            monthlyTotals,
            average,
        };
    }, [chartBatchResult?.results, comparisonUnits, monthColumns, selectedUnits]);

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    const toggleUnitExpansion = (unitId: string) => {
        setExpandedUnitIds((current) => {
            const next = new Set(current);
            if (next.has(unitId)) {
                next.delete(unitId);
            } else {
                next.add(unitId);
            }
            return next;
        });
    };

    return (
        <div className="space-y-6">
            <div className="space-y-6">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                    <div />
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleSelectAll}
                            className="whitespace-nowrap text-xs text-primary hover:underline"
                        >
                            Select all
                        </button>
                        <DropdownCheckbox
                            selected={selectedUnits}
                            onFilter={(selected) => setSelectedUnits(selected)}
                            data={comparisonUnits}
                            label="Units"
                        />
                    </div>
                </div>

                {loading && <p className="text-sm text-gray-500">Loading revenue by unit...</p>}
                {!loading && error && <p className="text-sm text-red-500">Unable to load revenue by unit.</p>}
                {!loading && !error && selectedUnits.length === 0 && (
                    <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
                )}

                {!loading && !error && selectedUnits.length > 0 && (
                    <div className="space-y-6">
                        {chartLoading && <p className="text-sm text-gray-500">Loading monthly revenue chart...</p>}
                        {!chartLoading && chartError && <p className="text-sm text-red-500">Unable to load monthly revenue chart.</p>}
                        {!chartLoading && !chartError && (
                            <div className="space-y-4">
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pr-4">
                                    {revenueChart.visibleUnits.map((unit) => (
                                        <div key={String(unit.value)} className="inline-flex items-center gap-2 text-sm text-black dark:text-white">
                                            <span
                                                className="h-3 w-6 rounded-sm"
                                                style={{ backgroundColor: revenueChart.colorByUnitId[String(unit.value)] }}
                                            />
                                            <span>{unit.label}</span>
                                        </div>
                                    ))}
                                    <div className="inline-flex items-center gap-2 text-sm text-black dark:text-white">
                                        <span className="relative block h-3 w-8">
                                            <span className="absolute inset-x-0 top-1/2 border-t-2 border-dashed border-slate-600" />
                                        </span>
                                        <span>Average</span>
                                    </div>
                                </div>

                                <ECharts
                                    height={360}
                                    xAxis={{
                                        type: "category",
                                        data: revenueChart.labels,
                                        boundaryGap: true,
                                        axisLabel: {
                                            rotate: 45,
                                        },
                                    }}
                                    yAxis={{
                                        type: "value",
                                        axisLabel: {
                                            formatter: (value: number) =>
                                                value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`,
                                        },
                                    }}
                                    series={revenueChart.series}
                                    color={comparisonUnits.map((unit, index) => REVENUE_CHART_COLORS[index % REVENUE_CHART_COLORS.length])}
                                    legend={{
                                        show: false,
                                    }}
                                    toolbox={{
                                        top: 0,
                                        right: 0,
                                    }}
                                    tooltip={{
                                        trigger: "axis",
                                        formatter: (params: any) => {
                                            if (!Array.isArray(params) || params.length === 0) return "";
                                            const dataIndex = params[0]?.dataIndex ?? 0;
                                            let result = `<strong>${params[0]?.axisValue}</strong><br/>`;
                                            params.forEach((item: any) => {
                                                const value = Number(item.value || 0).toLocaleString("en-US", {
                                                    style: "currency",
                                                    currency: "USD",
                                                    currencyDisplay: "symbol",
                                                    maximumFractionDigits: 0,
                                                });
                                                result += `${item.marker} ${item.seriesName}: ${value}<br/>`;
                                            });
                                            const total = revenueChart.monthlyTotals[dataIndex] ?? 0;
                                            const average = revenueChart.average ?? 0;
                                            const delta = total - average;
                                            const deltaLabel = `${delta > 0 ? "+" : ""}${delta.toLocaleString("en-US", {
                                                style: "currency",
                                                currency: "USD",
                                                currencyDisplay: "symbol",
                                                maximumFractionDigits: 0,
                                            })}`;
                                            result += `<strong>Month Total: ${total.toLocaleString("en-US", {
                                                style: "currency",
                                                currency: "USD",
                                                currencyDisplay: "symbol",
                                                maximumFractionDigits: 0,
                                            })}</strong>`;
                                            result += `<br/><strong>Delta vs Average: ${deltaLabel}</strong>`;
                                            return result;
                                        },
                                    }}
                                    grid={{
                                        left: 60,
                                        right: 20,
                                        top: 36,
                                        bottom: 80,
                                    }}
                                    dataZoom={[]}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="rounded-sm border border-stroke bg-gray-50 p-5 dark:border-strokedark dark:bg-meta-4">
                    <div className="space-y-5">
                        <div className="rounded-sm border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
                            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
                                <div className="space-y-2">
                                    <p className="text-sm font-semibold text-black dark:text-white">
                                        Revenue Drilldown
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Unit rows show RevPAU and ARPU. Expand a unit row to view RevPAR and ARPR by room.
                                    </p>
                                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                        Table values: top row = RevPAU, second row = ARPU
                                    </p>
                                </div>

                                <div className="space-y-2 border-t border-stroke pt-4 text-sm text-gray-500 dark:border-strokedark dark:text-gray-400 xl:border-l xl:border-t-0 xl:pl-5 xl:pt-0">
                                    <p><span className="font-medium text-black dark:text-white">RevPAU</span> = revenue for each available unit</p>
                                    <p><span className="font-medium text-black dark:text-white">ARPU</span> = RevPAU / active months</p>
                                    <p><span className="font-medium text-black dark:text-white">RevPAR</span> = revenue for each available room</p>
                                    <p><span className="font-medium text-black dark:text-white">ARPR</span> = RevPAR / active months</p>
                                    <p>Revenue is prorated based on actual days occupied within the period.</p>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto rounded-sm border border-stroke bg-white dark:border-strokedark dark:bg-boxdark">
                            <table className="w-full table-auto">
                                <thead>
                                    <tr className="bg-gray-100 dark:bg-meta-4">
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            Unit / Room
                                        </th>
                                        {columns.map((column) => (
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <RevenueUnitRow
                                            key={row.unitId}
                                            row={row}
                                            columns={columns}
                                            isExpanded={expandedUnitIds.has(row.unitId)}
                                            onToggle={() => toggleUnitExpansion(row.unitId)}
                                            formatValue={formatValue}
                                            formatMonthlyValue={formatMonthlyValue}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Component for rendering a single unit row with independent room data fetching
function OccupancyUnitRow({
    row,
    columns,
    isExpanded,
    onToggle,
    formatValue,
}: {
    row: { unitId: string; label: string; values: Record<string, number | null> };
    columns: Array<{ key: string; label: string; range: string }>;
    isExpanded: boolean;
    onToggle: () => void;
    formatValue: (value: number | null | undefined, format: "percent" | "currency" | "days" | "number") => string;
}) {
    // Fetch rooms for this specific unit when expanded
    const { data: unitRoomsResponse } = useGetKpiUnitRoomsQuery(row.unitId, {
        skip: !isExpanded,
    });

    const expandedRooms = useMemo(
        () => sortRooms((unitRoomsResponse?.rooms || []).filter((room) => room.id)),
        [unitRoomsResponse?.rooms]
    );

    const roomBatchArgs = useMemo(
        () => ({
            business_unit_id: row.unitId,
            room_ids: expandedRooms.map((room) => String(room.id)).sort((a, b) => a.localeCompare(b)),
            ranges: columns.map((column) => column.range),
            metrics: ["occupancy_rate"],
        }),
        [columns, expandedRooms, row.unitId]
    );

    const {
        data: roomBatchResponse,
        isFetching: isRoomLoading,
        error: roomError,
    } = useGetBatchRoomOperationalKpisDataQuery(roomBatchArgs, {
        skip: !isExpanded || expandedRooms.length === 0,
    });

    const roomRows = useMemo(
        () =>
            sortRooms(expandedRooms)
                .filter((room) => room.id)
                .map((room) => ({
                    roomId: String(room.id),
                    label: room.code,
                    values: Object.fromEntries(
                        columns.map((column) => {
                            const key = `${room.id}_${column.range}`;
                            const data = roomBatchResponse?.results[key];
                            return [column.key, data?.occupancy_rate ?? null];
                        })
                    ),
                })),
        [columns, expandedRooms, roomBatchResponse?.results]
    );

    return (
        <Fragment key={row.unitId}>
            <tr
                className="cursor-pointer border-t border-stroke hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4/20"
                onClick={onToggle}
            >
                <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                    <div className="flex items-center gap-3 text-left">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stroke text-xs font-semibold text-bodydark2 dark:border-strokedark">
                            {isExpanded ? "-" : "+"}
                        </span>
                        <span>{row.label}</span>
                    </div>
                </td>
                {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                        {formatValue(row.values[column.key], "percent")}
                    </td>
                ))}
            </tr>
            {isExpanded && isRoomLoading && (
                <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                    <td colSpan={columns.length + 1} className="px-4 py-3 text-sm text-gray-500">
                        Loading room occupancy...
                    </td>
                </tr>
            )}
            {isExpanded && !isRoomLoading && roomError && (
                <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                    <td colSpan={columns.length + 1} className="px-4 py-3 text-sm text-red-500">
                        Unable to load room occupancy.
                    </td>
                </tr>
            )}
            {isExpanded &&
                !isRoomLoading &&
                !roomError &&
                roomRows.map((roomRow) => (
                    <tr
                        key={`${row.unitId}:${roomRow.roomId}`}
                        className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30"
                    >
                        <td className="px-4 py-3 pl-14 text-sm text-black dark:text-white">
                            {roomRow.label}
                        </td>
                        {columns.map((column) => (
                            <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                {formatValue(roomRow.values[column.key], "percent")}
                            </td>
                        ))}
                    </tr>
                ))}
        </Fragment>
    );
}

export function OccupancyNestedDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
}: OccupancyNestedDrilldownProps) {
    const STORAGE_KEY = "kpi_occupancy_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
    const columns = useMemo(() => getLatestFiveCalendarYearColumns(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () => comparisonUnits.filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default (all units)
        setSelectedUnits(defaultSelectedUnits);
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);

    const buildRoomRowsFromBatch = (rooms: KpiRoomInfo[], metric: "occupancy_rate" | "vacant_days", batchResults?: Record<string, OperationalKpisRes>) =>
        sortRooms(rooms)
            .filter((room) => room.id)
            .map((room) => ({
                roomId: String(room.id),
                label: room.code,
                values: Object.fromEntries(
                    columns.map((column) => [
                        column.key,
                        batchResults?.[`${String(room.id)}_${column.range}`]?.[metric] ?? null,
                    ])
                ),
            }));
    // Fetch ALL available units (not just selected) to enable local filtering
    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );
    const unitBatchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
            metrics: ["occupancy_rate"],
        }),
        [columns, allUnitIds]
    );
    const { data: unitBatchResponse, isFetching: loading, error } = useGetBatchOperationalKpisDataQuery(unitBatchArgs, {
        skip: allUnitIds.length === 0,
    });
    // Filter rows locally based on selectedUnits
    const rows = useMemo(
        () => {
            const selectedUnitIds = new Set(selectedUnits.map(u => String(u.value)));
            return comparisonUnits
                .filter(unit => selectedUnitIds.has(String(unit.value)))
                .map((unit) => {
                    const unitId = String(unit.value);
                    const valuesEntries = columns.map((column) => [
                        column.key,
                        unitBatchResponse?.results[`${unitId}_${column.range}`]?.occupancy_rate ?? null,
                    ] as const);

                    return {
                        unitId,
                        label: String(unit.label),
                        values: Object.fromEntries(valuesEntries),
                    };
                });
        },
        [columns, comparisonUnits, selectedUnits, unitBatchResponse?.results]
    );

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    const toggleUnitExpansion = (unitId: string) => {
        setExpandedUnitIds((current) => {
            const next = new Set(current);
            if (next.has(unitId)) {
                next.delete(unitId);
            } else {
                next.add(unitId);
            }
            return next;
        });
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare the selected units across the current year to date and the previous 4 full calendar years. Expand a unit row to compare room-level occupancy across the same years.
                </p>
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {loading && <p className="text-sm text-gray-500">Loading occupancy by unit...</p>}
            {!loading && error && <p className="text-sm text-red-500">Unable to load occupancy by unit.</p>}
            {!loading && !error && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Unit / Room
                                </th>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <OccupancyUnitRow
                                    key={row.unitId}
                                    row={row}
                                    columns={columns}
                                    isExpanded={expandedUnitIds.has(row.unitId)}
                                    onToggle={() => toggleUnitExpansion(row.unitId)}
                                    formatValue={formatValue}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Component for rendering a single vacant days unit row with independent room data fetching
function VacantDaysUnitRow({
    row,
    columns,
    isExpanded,
    onToggle,
    formatValue,
}: {
    row: { unitId: string; label: string; values: Record<string, number | null> };
    columns: Array<{ key: string; label: string; range: string }>;
    isExpanded: boolean;
    onToggle: () => void;
    formatValue: (value: number | null | undefined, format: "percent" | "currency" | "days" | "number") => string;
}) {
    const { data: unitRoomsResponse } = useGetKpiUnitRoomsQuery(row.unitId, {
        skip: !isExpanded,
    });

    const expandedRooms = useMemo(
        () => (unitRoomsResponse?.rooms || []).filter((room) => room.id),
        [unitRoomsResponse?.rooms]
    );

    const roomBatchArgs = useMemo(
        () => ({
            business_unit_id: row.unitId,
            room_ids: expandedRooms.map((room) => String(room.id)).sort((a, b) => a.localeCompare(b)),
            ranges: columns.map((column) => column.range),
            metrics: ["vacant_days"],
        }),
        [columns, expandedRooms, row.unitId]
    );

    const {
        data: roomBatchResponse,
        isFetching: isRoomLoading,
        error: roomError,
    } = useGetBatchRoomOperationalKpisDataQuery(roomBatchArgs, {
        skip: !isExpanded || expandedRooms.length === 0,
    });

    const roomRows = useMemo(
        () =>
            sortRooms(expandedRooms)
                .filter((room) => room.id)
                .map((room) => ({
                    roomId: String(room.id),
                    label: room.code,
                    values: Object.fromEntries(
                        columns.map((column) => {
                            const key = `${room.id}_${column.range}`;
                            const data = roomBatchResponse?.results[key];
                            return [column.key, data?.vacant_days ?? null];
                        })
                    ),
                })),
        [columns, expandedRooms, roomBatchResponse?.results]
    );

    return (
        <Fragment key={row.unitId}>
            <tr
                className="cursor-pointer border-t border-stroke hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4/20"
                onClick={onToggle}
            >
                <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                    <div className="flex items-center gap-3 text-left">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stroke text-xs font-semibold text-bodydark2 dark:border-strokedark">
                            {isExpanded ? "-" : "+"}
                        </span>
                        <span>{row.label}</span>
                    </div>
                </td>
                {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                        {formatValue(row.values[column.key], "days")}
                    </td>
                ))}
            </tr>
            {isExpanded && isRoomLoading && (
                <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                    <td colSpan={columns.length + 1} className="px-4 py-3 text-sm text-gray-500">
                        Loading room vacant days...
                    </td>
                </tr>
            )}
            {isExpanded && !isRoomLoading && roomError && (
                <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                    <td colSpan={columns.length + 1} className="px-4 py-3 text-sm text-red-500">
                        Unable to load room vacant days.
                    </td>
                </tr>
            )}
            {isExpanded &&
                !isRoomLoading &&
                !roomError &&
                roomRows.map((roomRow) => (
                    <tr key={roomRow.roomId} className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                        <td className="px-4 py-3 pl-14 text-sm text-black dark:text-white">{roomRow.label}</td>
                        {columns.map((column) => (
                            <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                {formatValue(roomRow.values[column.key], "days")}
                            </td>
                        ))}
                    </tr>
                ))}
        </Fragment>
    );
}

export function VacantDaysDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
}: OccupancyNestedDrilldownProps) {
    const STORAGE_KEY = "kpi_vacant_days_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
    const columns = useMemo(() => getLatestFiveCalendarYearColumns(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () => comparisonUnits.filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default (all units)
        setSelectedUnits(defaultSelectedUnits);
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);

    const buildRoomRowsFromBatch = (rooms: KpiRoomInfo[], batchResults?: Record<string, OperationalKpisRes>) =>
        sortRooms(rooms)
            .filter((room) => room.id)
            .map((room) => ({
                roomId: String(room.id),
                label: room.code,
                values: Object.fromEntries(
                    columns.map((column) => [
                        column.key,
                        batchResults?.[`${String(room.id)}_${column.range}`]?.vacant_days ?? 0,
                    ])
                ),
            }));
    // Fetch ALL available units (not just selected) to enable local filtering
    const allUnitIds = useMemo(
        () => comparisonUnits.map((unit) => String(unit.value)).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );
    const unitBatchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: columns.map((column) => column.range),
            metrics: ["vacant_days"],
        }),
        [columns, allUnitIds]
    );
    const { data: unitBatchResponse, isFetching: loading, error } = useGetBatchOperationalKpisDataQuery(unitBatchArgs, {
        skip: allUnitIds.length === 0,
    });
    // Filter rows locally based on selectedUnits
    const rows = useMemo(
        () => {
            const selectedUnitIds = new Set(selectedUnits.map(u => String(u.value)));
            return comparisonUnits
                .filter(unit => selectedUnitIds.has(String(unit.value)))
                .map((unit) => {
                    const unitId = String(unit.value);
                    const valuesEntries = columns.map((column) => [
                        column.key,
                        unitBatchResponse?.results[`${unitId}_${column.range}`]?.vacant_days ?? null,
                    ] as const);

                    return {
                        unitId,
                        label: String(unit.label),
                        values: Object.fromEntries(valuesEntries),
                    };
                });
        },
        [columns, comparisonUnits, selectedUnits, unitBatchResponse?.results]
    );

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    const toggleUnitExpansion = (unitId: string) => {
        setExpandedUnitIds((current) => {
            const next = new Set(current);
            if (next.has(unitId)) {
                next.delete(unitId);
            } else {
                next.add(unitId);
            }
            return next;
        });
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare vacant days by unit across the current year to date and the previous 4 full calendar years. Expand a unit row to compare room-level vacant days across the same years.
                </p>
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {loading && <p className="text-sm text-gray-500">Loading vacant days by unit...</p>}
            {!loading && error && <p className="text-sm text-red-500">Unable to load vacant days by unit.</p>}
            {!loading && !error && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Unit / Room
                                </th>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <VacantDaysUnitRow
                                    key={row.unitId}
                                    row={row}
                                    columns={columns}
                                    isExpanded={expandedUnitIds.has(row.unitId)}
                                    onToggle={() => toggleUnitExpansion(row.unitId)}
                                    formatValue={formatValue}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
// Component for rendering a single maintenance unit row with independent chart and transaction data
function MaintenanceUnitRow({
    row,
    columns,
    isExpanded,
    onToggle,
    formatValue,
    panelDateRange,
    panelCustomRange,
    businessUnits,
}: {
    row: { unitId: string; label: string; values: Record<string, number | null> };
    columns: Array<{ key: string; label: string; range: string }>;
    isExpanded: boolean;
    onToggle: () => void;
    formatValue: (value: number | null | undefined, format: "percent" | "currency" | "days" | "number") => string;
    panelDateRange: string;
    panelCustomRange: { start: string; end: string };
    businessUnits: Array<{ value: string | number; label: string }>;
}) {
    const detailMaintenanceBatchArgs = useMemo(
        () => ({
            ranges: [panelDateRange],
            metrics: ["monthly_maintenance"],
        }),
        [panelDateRange]
    );

    const { data: detailMaintenanceBatchResponse, isFetching: isLoading } = useGetBatchCompanyOperationalKpisDataQuery(
        detailMaintenanceBatchArgs,
        {
            skip: !isExpanded,
        }
    );

    const { data: maintenanceTransactionsData, isLoading: maintenanceTransactionsLoading } = useGetExpenseBreakdownQuery(
        {
            range: panelDateRange,
            start: panelDateRange === "custom" ? panelCustomRange.start : undefined,
            end: panelDateRange === "custom" ? panelCustomRange.end : undefined,
            scope: "property_related",
            dimension: "unit",
            business_unit_id: row.unitId,
            codes: ["HANDY"],
        },
        { skip: !isExpanded }
    );

    const detailMaintenance = detailMaintenanceBatchResponse?.results?.[panelDateRange];
    const monthlyMaintenance = detailMaintenance?.monthly_maintenance || [];
    const maintenanceUnits = detailMaintenance?.maintenance_units || [];

    const expandedUnitLabel =
        businessUnits.find((unit) => String(unit.value) === row.unitId)?.label || "Selected unit";

    const chartData = useMemo(() => {
        const months = monthlyMaintenance.map((point) => point.month);
        const selectedUnit = maintenanceUnits.find((unit) => unit.id === row.unitId);
        const unitSeries = selectedUnit
            ? [
                  {
                      name: selectedUnit.name,
                      type: "bar",
                      data: monthlyMaintenance.map((point) => point.unit_data[selectedUnit.id] || 0),
                  },
              ]
            : [];

        const totalAmount = monthlyMaintenance.reduce((sum, point) => sum + (point.unit_data[row.unitId] || 0), 0);
        const averageMonthly = monthlyMaintenance.length > 0 ? totalAmount / monthlyMaintenance.length : 0;

        return {
            months,
            series: unitSeries,
            totalAmount,
            averageMonthly,
        };
    }, [row.unitId, maintenanceUnits, monthlyMaintenance]);

    return (
        <Fragment key={row.unitId}>
            <tr
                className="cursor-pointer border-t border-stroke hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4/20"
                onClick={onToggle}
            >
                <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                    <div className="flex items-center gap-3 text-left">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stroke text-xs font-semibold text-bodydark2 dark:border-strokedark">
                            {isExpanded ? "-" : "+"}
                        </span>
                        <span>{row.label}</span>
                    </div>
                </td>
                {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                        {formatValue(row.values[column.key], "currency")}
                    </td>
                ))}
            </tr>
            {isExpanded && (
                <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                    <td colSpan={columns.length + 1} className="px-6 py-6">
                        <div className="space-y-6">
                            {isLoading ? (
                                <div className="flex h-32 items-center justify-center">
                                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <SummaryCard
                                            label="Total Maintenance"
                                            value={chartData.totalAmount}
                                            format="currency"
                                        />
                                        <SummaryCard
                                            label="Average Monthly Maintenance"
                                            value={chartData.averageMonthly}
                                            format="currency"
                                        />
                                    </div>

                                    <ECharts
                                        height={360}
                                        xAxis={{
                                            type: "category",
                                            data: chartData.months,
                                            boundaryGap: true,
                                            axisLabel: {
                                                rotate: 45,
                                            },
                                        }}
                                        yAxis={{
                                            type: "value",
                                            axisLabel: {
                                                formatter: (value: number) =>
                                                    value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`,
                                            },
                                        }}
                                        series={chartData.series}
                                        legend={{
                                            show: false,
                                        }}
                                        tooltip={{
                                            trigger: "axis",
                                            formatter: (params: any) => {
                                                if (!Array.isArray(params)) return "";
                                                let result = `<strong>${params[0]?.axisValue}</strong><br/>`;
                                                params.forEach((item: any) => {
                                                    const formatted = Number(item.value || 0).toLocaleString("en-US", {
                                                        style: "currency",
                                                        currency: "USD",
                                                        currencyDisplay: "symbol",
                                                        maximumFractionDigits: 0,
                                                    });
                                                    result += `${item.marker} ${item.seriesName}: ${formatted}<br/>`;
                                                });
                                                return result;
                                            },
                                        }}
                                        grid={{
                                            left: 60,
                                            right: 20,
                                            bottom: 70,
                                            top: 50,
                                        }}
                                        dataZoom={[]}
                                    />
                                </>
                            )}

                            <div className="border-t border-stroke pt-4 dark:border-strokedark">
                                <p className="text-sm font-medium text-black dark:text-white">
                                    {expandedUnitLabel} maintenance transactions
                                </p>
                                <p className="mt-1 text-xs text-gray-500">
                                    Showing `HANDY` transactions for the selected period.
                                </p>

                                <div className="mt-4 overflow-x-auto">
                                    {maintenanceTransactionsLoading ? (
                                        <div className="flex h-32 items-center justify-center">
                                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
                                        </div>
                                    ) : (
                                        <table className="w-full table-auto">
                                            <thead>
                                                <tr className="bg-gray-100 text-left dark:bg-meta-4">
                                                    <th className="px-4 py-4 font-medium text-black dark:text-white">Date</th>
                                                    <th className="px-4 py-4 font-medium text-black dark:text-white">Code</th>
                                                    <th className="px-4 py-4 font-medium text-black dark:text-white">
                                                        Description
                                                    </th>
                                                    <th className="px-4 py-4 text-right font-medium text-black dark:text-white">
                                                        Amount
                                                    </th>
                                                    <th className="px-4 py-4 font-medium text-black dark:text-white">Type</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(maintenanceTransactionsData?.transactions || []).length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                                                            No maintenance transactions found for this period.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    (maintenanceTransactionsData?.transactions || []).map((transaction, idx) => (
                                                        <tr
                                                            key={idx}
                                                            className="border-b border-stroke dark:border-strokedark"
                                                        >
                                                            <td className="px-4 py-3 text-sm text-black dark:text-white">
                                                                {transaction.date}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-black dark:text-white">
                                                                {transaction.code}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-black dark:text-white">
                                                                {transaction.description}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-sm text-black dark:text-white">
                                                                {formatValue(transaction.amount, "currency")}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm">
                                                                <span
                                                                    className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                                                                        transaction.type === "CAPEX"
                                                                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                                                                            : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                                                    }`}
                                                                >
                                                                    {transaction.type}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </Fragment>
    );
}

export function MaintenanceDrilldown({
    monthlyMaintenance: _monthlyMaintenance,
    maintenanceUnits: _maintenanceUnits,
    inactiveUnitIds,
    helperNote: _helperNote,
}: MaintenanceDrilldownProps) {
    const maintenanceDateRangeOptions = useMemo(
        () => {
            const currentYear = new Date().getFullYear();

            return [
                { value: "current_year_through_last_completed_month", label: "YTD (until last completed month)" },
                ...Array.from({ length: 5 }, (_, index) => {
                    const year = currentYear - 1 - index;
                    return { value: `year_${year}`, label: String(year) };
                }),
            ];
        },
        []
    );
    const STORAGE_KEY = "kpi_maintenance_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
    const [panelDateRange, setPanelDateRange] = useState("current_year_through_last_completed_month");
    const [panelCustomRange, setPanelCustomRange] = useState({ start: "", end: "" });
    const { data: properties = [] } = useGetPropertiesQuery();
    const businessUnits = useMemo(
        () =>
            properties
                .filter((property) => !DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(property.name).trim().toUpperCase()))
                .map((property) => ({ value: property.id, label: property.name })),
        [properties]
    );
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(() => comparisonUnits, [comparisonUnits]);

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default (all units)
        setSelectedUnits(defaultSelectedUnits);
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);

    const columns = useMemo(() => getMaintenanceColumns(), []);
    const maintenanceBatchArgs = useMemo(
        () => ({
            ranges: columns.map((column) => column.range),
            metrics: ["monthly_maintenance"],
        }),
        [columns]
    );
    const {
        data: maintenanceBatchResponse,
        isFetching: tableLoading,
        error: tableError,
    } = useGetBatchCompanyOperationalKpisDataQuery(maintenanceBatchArgs, {
    });
    const rows = useMemo(
        () =>
            selectedUnits.map((unit) => {
                const unitId = String(unit.value);
                const values = Object.fromEntries(
                    columns.map((column) => {
                        const monthlyPoints = maintenanceBatchResponse?.results[column.range]?.monthly_maintenance || [];
                        const total = monthlyPoints.reduce(
                            (sum, point) => sum + (point.unit_data?.[unitId] || 0),
                            0
                        );
                        return [column.key, total];
                    })
                );

                return {
                    unitId,
                    label: String(unit.label),
                    values,
                };
            }),
        [columns, maintenanceBatchResponse?.results, selectedUnits]
    );

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    const toggleUnitExpansion = (unitId: string) => {
        setExpandedUnitIds((current) => {
            const next = new Set(current);
            if (next.has(unitId)) {
                next.delete(unitId);
            } else {
                next.add(unitId);
            }
            return next;
        });
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">
                    Maintenance Cost Detail
                </h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare maintenance cost by unit across the current year through the end of the last completed month and the previous 4 full calendar years. Expand a unit row to see the monthly HANDY chart and HANDY transactions for that unit.
                </p>
                {_helperNote && (
                    <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                        {_helperNote}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {tableLoading && <p className="text-sm text-gray-500">Loading maintenance cost history...</p>}
            {!tableLoading && tableError && <p className="text-sm text-red-500">Unable to load maintenance cost history.</p>}
            {!tableLoading && !tableError && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!tableLoading && !tableError && selectedUnits.length > 0 && (
                <div className="overflow-x-auto rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Unit
                                </th>
                                {columns.map((column) => (
                                    <th key={column.key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <MaintenanceUnitRow
                                    key={row.unitId}
                                    row={row}
                                    columns={columns}
                                    isExpanded={expandedUnitIds.has(row.unitId)}
                                    onToggle={() => toggleUnitExpansion(row.unitId)}
                                    formatValue={formatValue}
                                    panelDateRange={panelDateRange}
                                    panelCustomRange={panelCustomRange}
                                    businessUnits={businessUnits}
                                />
                            ))}
                            {rows.length > 0 && (
                                <tr className="border-t-2 border-stroke bg-gray-100 font-semibold dark:border-strokedark dark:bg-meta-4">
                                    <td className="px-4 py-3 text-sm text-black dark:text-white">Total ({rows.length} units)</td>
                                    {columns.map((column) => {
                                        const total = rows.reduce((sum, row) => sum + (row.values[column.key] || 0), 0);
                                        return (
                                            <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                                {formatValue(total, "currency")}
                                            </td>
                                        );
                                    })}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function UtilityUnitRow({
    row,
    latestTwelveMonths,
    businessUnits,
    formatValue,
    monthToDate,
    isExpanded,
    onToggle,
    selectedProviders,
    setSelectedProviders,
}: {
    row: {
        unitId: string;
        label: string;
        averageMonthlyLast24Months: number | null;
        latestMonthValues: Record<string, number>;
        operationalMonths: number;
        firstMonthWithData: Date | null;
        lastMonthWithData: Date | null;
    };
    latestTwelveMonths: any[];
    businessUnits: IDropdown[];
    formatValue: (value: number | null | undefined, format: "percent" | "currency" | "days" | "number") => string;
    monthToDate: (monthStr: string) => Date;
    isExpanded: boolean;
    onToggle: () => void;
    selectedProviders: string[];
    setSelectedProviders: (providers: string[]) => void;
}) {
    // Fetch utility data for this specific unit
    const { data: unitUtilityBatchResponse, isFetching: unitUtilityLoading } = useGetBatchUtilitiesKpisDataQuery(
        {
            requests: [
                {
                    key: `${row.unitId}_last24`,
                    range: "last_24_months",
                    property_ids: [row.unitId],
                },
            ],
        },
        { skip: !isExpanded }
    );

    const unitUtilityData = unitUtilityBatchResponse?.results?.[`${row.unitId}_last24`];

    // Track if we've initialized providers for this unit
    const hasInitializedProvidersRef = useRef(false);

    // Initialize all providers as selected when data first loads
    useEffect(() => {
        if (unitUtilityData?.providers && unitUtilityData.providers.length > 0 && !hasInitializedProvidersRef.current) {
            // Merge current selection with this unit's providers (only once)
            const merged = new Set([...selectedProviders, ...unitUtilityData.providers]);
            setSelectedProviders(Array.from(merged));
            hasInitializedProvidersRef.current = true;
        }
    }, [unitUtilityData?.providers]);

    // Provider filtering state
    const selectedProviderSet = useMemo(() => new Set(selectedProviders), [selectedProviders]);

    // Compute chart data for this unit
    const unitChartData = useMemo(() => {
        if (!unitUtilityData?.monthly_data) {
            return { months: [], series: [], providerTotals: {}, providerColors: {}, average: 0, monthlyTotals: [] };
        }

        const months = unitUtilityData.monthly_data.map((item) => item.month);
        const visibleProviders = unitUtilityData.providers.filter((p) => selectedProviderSet.has(p));
        const monthlyTotals = unitUtilityData.monthly_data.map((item) =>
            visibleProviders.reduce((sum, provider) => sum + (item.provider_data?.[provider] || 0), 0)
        );

        const providerTotals: Record<string, number> = {};
        unitUtilityData.providers.forEach((provider) => {
            providerTotals[provider] = unitUtilityData.monthly_data.reduce((sum, item) => sum + (item.provider_data?.[provider] || 0), 0);
        });

        const providerColors: Record<string, string> = {};
        const palette = ["#5470c6", "#91cc75", "#fac858", "#ee6666", "#73c0de", "#3ba272", "#fc8452", "#9a60b4", "#ea7ccc"];
        unitUtilityData.providers.forEach((provider, index) => {
            providerColors[provider] = palette[index % palette.length];
        });

        let operationalMonths = unitUtilityData.operational_month_counts?.[row.unitId || ''];

        if (!operationalMonths && row.unitId && unitUtilityData.operational_months?.[row.unitId]) {
            // Use operational_months map
            operationalMonths = Object.keys(unitUtilityData.operational_months[row.unitId]).length;
        }

        if (!operationalMonths) {
            // Last resort: count months with actual utility data
            const totalUtilities = monthlyTotals.reduce((sum, value) => sum + value, 0);
            if (totalUtilities > 0) {
                operationalMonths = monthlyTotals.filter(value => value > 0).length;
            } else {
                operationalMonths = 0;
            }
        }

        const average = operationalMonths > 0
            ? monthlyTotals.reduce((sum, value) => sum + value, 0) / operationalMonths
            : 0;

        const series = [
            ...visibleProviders.map((provider, index) => ({
                name: provider,
                type: "bar" as const,
                stack: "utilities",
                data: unitUtilityData.monthly_data.map((item) => item.provider_data?.[provider] || 0),
                itemStyle: {
                    color: providerColors[provider],
                    borderRadius: index === visibleProviders.length - 1 ? [4, 4, 0, 0] as [number, number, number, number] : [0, 0, 0, 0] as [number, number, number, number],
                },
            })),
            {
                name: "Average",
                type: "line" as const,
                data: months.map(() => average),
                symbol: "none" as const,
                lineStyle: { type: "dashed" as const, width: 2 },
                itemStyle: { color: "#91cc75" },
            },
        ];

        return { months, series, providerTotals, providerColors, average, monthlyTotals };
    }, [unitUtilityData, selectedProviderSet, row.unitId]);

    const unitLabel = businessUnits.find((unit) => String(unit.value) === row.unitId)?.label || row.label;

    const toggleProvider = (provider: string) => {
        setSelectedProviders(
            selectedProviders.includes(provider)
                ? selectedProviders.filter((p) => p !== provider)
                : [...selectedProviders, provider]
        );
    };

    return (
        <Fragment>
            <tr
                className="cursor-pointer border-t border-stroke hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4/20"
                onClick={() => onToggle()}
            >
                <td className="px-3 py-3 text-sm font-medium text-black dark:text-white">
                    <div className="flex items-center gap-3 text-left">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stroke text-xs font-semibold text-bodydark2 dark:border-strokedark">
                            {isExpanded ? "-" : "+"}
                        </span>
                        <span className="truncate">{row.label}</span>
                    </div>
                </td>
                <td className="px-2 py-3 text-center text-sm font-semibold text-black dark:text-white">
                    <div className="flex flex-col items-center">
                        <span>{formatValue(row.averageMonthlyLast24Months, "currency")}</span>
                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                            ({row.operationalMonths} months)
                        </span>
                    </div>
                </td>
                {latestTwelveMonths.map((point) => {
                    const monthValue = row.latestMonthValues[point.month];
                    const avgValue = row.averageMonthlyLast24Months;

                    // Check if month is within the operational period (first to last month with data)
                    const pointDate = monthToDate(point.month);
                    const isOperational = row.firstMonthWithData && row.lastMonthWithData
                        ? (pointDate >= row.firstMonthWithData && pointDate <= row.lastMonthWithData)
                        : false;

                    // Only show delta if the month is operational
                    const deltaPct =
                        isOperational && avgValue && avgValue !== 0
                            ? ((monthValue - avgValue) / avgValue) * 100
                            : null;
                    const deltaClass =
                        deltaPct == null || Math.abs(deltaPct) < 0.05
                            ? "text-gray-500"
                            : deltaPct < 0
                              ? "text-green-600"
                              : "text-red-600";
                    const cellBgClass =
                        deltaPct == null || Math.abs(deltaPct) < 0.05
                            ? ""
                            : deltaPct < 0
                              ? "bg-green-50 dark:bg-green-950/20"
                              : "bg-red-50 dark:bg-red-950/20";

                    return (
                        <td key={point.month} className={`px-1 py-2 text-center ${cellBgClass}`}>
                            <div className="text-[11px] font-medium text-black dark:text-white">
                                {!isOperational && monthValue === 0 ? "N/A" : formatValue(monthValue, "currency")}
                            </div>
                            <div className={`text-[10px] ${deltaClass}`}>
                                {deltaPct == null ? "-" : `${deltaPct > 0 ? "+" : ""}${deltaPct.toFixed(0)}%`}
                            </div>
                        </td>
                    );
                })}
            </tr>
            {isExpanded && (
                <tr className="border-t border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4/30">
                    <td colSpan={latestTwelveMonths.length + 2} className="px-4 py-5">
                        <div className="space-y-5">
                            <div>
                                <p className="text-sm font-medium text-black dark:text-white">
                                    {unitLabel} utility expenditure by provider
                                </p>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Click provider cards below to include or exclude vendors from the displayed 24-month average.
                                </p>
                            </div>

                            {unitUtilityLoading ? (
                                <p className="text-sm text-gray-500">Loading unit utility history...</p>
                            ) : !unitUtilityData?.monthly_data?.length ? (
                                <p className="text-sm text-gray-500">No utility history available for this unit.</p>
                            ) : (
                                <>
                                    <ECharts
                                        height={350}
                                        xAxis={{
                                            type: "category",
                                            data: unitChartData.months,
                                            boundaryGap: true,
                                            axisLabel: {
                                                rotate: 45,
                                                margin: 16,
                                            },
                                        }}
                                        yAxis={{
                                            type: "value",
                                            axisLabel: {
                                                formatter: (value: number) =>
                                                    value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`,
                                            },
                                        }}
                                        series={unitChartData.series}
                                        legend={{
                                            show: false,
                                        }}
                                        tooltip={{
                                            trigger: "axis",
                                            formatter: (params: any) => {
                                                if (!Array.isArray(params)) return "";
                                                const dataIndex = params[0]?.dataIndex ?? 0;
                                                let result = `<strong>${params[0]?.axisValue}</strong><br/>`;
                                                params.forEach((item: any) => {
                                                    const value = Number(item.value || 0).toLocaleString("en-US", {
                                                        style: "currency",
                                                        currency: "USD",
                                                        currencyDisplay: "symbol",
                                                        maximumFractionDigits: 0,
                                                    });
                                                    result += `${item.marker} ${item.seriesName}: ${value}<br/>`;
                                                });
                                                const totalValue = unitChartData.monthlyTotals[dataIndex] ?? 0;
                                                const averageValue = unitChartData.average || 0;
                                                const deltaPct =
                                                    averageValue !== 0
                                                        ? ((totalValue - averageValue) / averageValue) * 100
                                                        : null;
                                                result += `<strong>Month Total: ${totalValue.toLocaleString("en-US", {
                                                    style: "currency",
                                                    currency: "USD",
                                                    currencyDisplay: "symbol",
                                                    maximumFractionDigits: 0,
                                                })}</strong>`;
                                                if (deltaPct != null) {
                                                    result += `<br/><strong>Delta vs Avg: ${deltaPct > 0 ? "+" : ""}${deltaPct.toFixed(0)}%</strong>`;
                                                }
                                                return result;
                                            },
                                        }}
                                        grid={{
                                            left: 78,
                                            right: 20,
                                            bottom: 60,
                                            top: 50,
                                        }}
                                        dataZoom={[]}
                                    />

                                    {unitUtilityData.providers && unitUtilityData.providers.length > 0 && (
                                        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                                            {unitUtilityData.providers.map((provider) => {
                                                const isSelected = selectedProviderSet.has(provider);
                                                return (
                                                    <button
                                                        key={provider}
                                                        type="button"
                                                        onClick={() => toggleProvider(provider)}
                                                        className={`rounded border p-3 text-left ${
                                                            isSelected
                                                                ? "border-primary bg-white dark:bg-boxdark"
                                                                : "border-stroke bg-gray-100 opacity-60 dark:border-strokedark dark:bg-meta-4"
                                                        }`}
                                                    >
                                                        <div className="mb-2 flex items-center gap-2">
                                                            <span
                                                                className="inline-block h-3 w-3 rounded-full"
                                                                style={{ backgroundColor: unitChartData.providerColors[provider] }}
                                                            />
                                                            <p className="text-xs text-gray-500">{provider}</p>
                                                        </div>
                                                        <p className="text-lg font-bold text-black dark:text-white">
                                                            {unitChartData.providerTotals[provider].toLocaleString("en-US", {
                                                                style: "currency",
                                                                currency: "USD",
                                                                currencyDisplay: "symbol",
                                                                maximumFractionDigits: 0,
                                                            })}
                                                        </p>
                                                    </button>
                                                );
                                            })}
                                            <div className="rounded bg-green-50 p-3 dark:bg-meta-4">
                                                <p className="text-xs text-gray-500">Average (selected providers)</p>
                                                <p className="text-lg font-bold text-meta-3">
                                                    {unitChartData.average.toLocaleString("en-US", {
                                                        style: "currency",
                                                        currency: "USD",
                                                        currencyDisplay: "symbol",
                                                        maximumFractionDigits: 0,
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </Fragment>
    );
}


export function UtilityExpenditureDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
}: UtilityExpenditureDrilldownProps) {
    const STORAGE_KEY = "kpi_utility_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
    const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () => comparisonUnits.filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        // Try to load from localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        // Fallback to default (all units)
        setSelectedUnits(defaultSelectedUnits);
    }, [comparisonUnits, defaultSelectedUnits]);

    // Save to localStorage whenever selection changes
    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);

    const companyBatchArgs = useMemo(
        () => ({
            requests: [
                { key: "ytd_company", range: "ytd" },
                { key: "last24_company", range: "last_24_months" },
            ],
        }),
        []
    );
    const {
        data: companyUtilitiesBatch,
        isFetching: companyUtilitiesLoading,
        error: companyUtilitiesError,
    } = useGetBatchUtilitiesKpisDataQuery(companyBatchArgs);
    const companyUtilitiesData = companyUtilitiesBatch?.results || {};
    const ytdData = companyUtilitiesData.ytd_company;
    const last24Data = companyUtilitiesData.last24_company;

    const latestTwelveMonths = useMemo(() => (last24Data?.monthly_data || []).slice(-12).reverse(), [last24Data?.monthly_data]);

    // Helper to convert "Jan 2024" to a sortable date value
    const monthToDate = (monthStr: string): Date => {
        return new Date(monthStr + " 01");
    };

    // Helper to convert "Jan 2024" to "01 2024" format
    const monthToKey = (monthStr: string): string => {
        const d = new Date(monthStr + " 01");
        return d.toLocaleDateString('en-US', { month: '2-digit', year: 'numeric' }).replace('/', ' ');
    };

    const rows = useMemo(() => {
        const last24Points = last24Data?.monthly_data || [];
        const operationalMonthCounts = last24Data?.operational_month_counts || {};
        const operationalMonthsData = last24Data?.operational_months || {};

        return selectedUnits.map((unit) => {
            const unitId = String(unit.value);
            const last24Total = last24Points.reduce((sum, point) => sum + (point.property_data?.[unitId] || 0), 0);
            const latestMonthValues = Object.fromEntries(
                latestTwelveMonths.map((point) => [point.month, point.property_data?.[unitId] || 0])
            );

            // Find first and last month with utility data for this unit
            const monthsWithData = last24Points
                .filter(point => (point.property_data?.[unitId] || 0) > 0)
                .map(point => point.month);

            let firstMonthWithData: Date | null = null;
            let lastMonthWithData: Date | null = null;
            let operationalMonthsSet = new Set<string>();

            if (monthsWithData.length > 0) {
                const sortedDates = monthsWithData.map(m => monthToDate(m)).sort((a, b) => a.getTime() - b.getTime());
                firstMonthWithData = sortedDates[0];
                lastMonthWithData = sortedDates[sortedDates.length - 1];

                // All months from first to last are operational (including gaps)
                last24Points.forEach(point => {
                    const pointDate = monthToDate(point.month);
                    if (firstMonthWithData && lastMonthWithData &&
                        pointDate >= firstMonthWithData && pointDate <= lastMonthWithData) {
                        operationalMonthsSet.add(monthToKey(point.month));
                    }
                });
            }

            // Also include months from backend operational_months (for active periods without utility)
            if (operationalMonthsData[unitId]) {
                Object.keys(operationalMonthsData[unitId]).forEach(month => {
                    operationalMonthsSet.add(month);
                });
            }

            // Use backend count if available, otherwise use our calculated set
            let operationalMonths = operationalMonthCounts[unitId] || operationalMonthsSet.size;

            return {
                unitId,
                label: String(unit.label),
                averageMonthlyLast24Months: operationalMonths > 0 ? last24Total / operationalMonths : null,
                latestMonthValues,
                operationalMonths: operationalMonths || 0,
                operationalMonthsSet, // Include for per-cell checking
                firstMonthWithData,
                lastMonthWithData,
            };
        });
    }, [last24Data?.monthly_data, last24Data?.operational_month_counts, last24Data?.operational_months, latestTwelveMonths, selectedUnits]);

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    const toggleUnitExpansion = (unitId: string) => {
        setExpandedUnitIds((current) => {
            const next = new Set(current);
            if (next.has(unitId)) {
                next.delete(unitId);
            } else {
                next.add(unitId);
            }
            return next;
        });
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Compare each unit's latest 12 months against its 24-month utility average, then expand a unit row to view the latest 24 months by provider using the existing parsed vendor logic.
                </p>
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {companyUtilitiesLoading && <p className="text-sm text-gray-500">Loading utility expenditure...</p>}
            {!companyUtilitiesLoading && companyUtilitiesError && <p className="text-sm text-red-500">Unable to load utility expenditure.</p>}
            {!companyUtilitiesLoading && !companyUtilitiesError && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to compare.</p>
            )}

            {!companyUtilitiesLoading && !companyUtilitiesError && selectedUnits.length > 0 && (
                <div className="rounded-sm border border-stroke dark:border-strokedark">
                    <table className="w-full table-fixed">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-meta-4">
                                <th className="w-44 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Unit
                                </th>
                                <th className="w-24 px-2 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Avg
                                </th>
                                {latestTwelveMonths.map((point) => {
                                    const { month, year } = splitMonthLabel(point.month);

                                    return (
                                        <th key={point.month} className="px-1 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                            <span className="block leading-tight">{month}</span>
                                            {year && <span className="block leading-tight">{year}</span>}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <UtilityUnitRow
                                    key={row.unitId}
                                    row={row}
                                    latestTwelveMonths={latestTwelveMonths}
                                    businessUnits={businessUnits}
                                    formatValue={formatValue}
                                    monthToDate={monthToDate}
                                    isExpanded={expandedUnitIds.has(row.unitId)}
                                    onToggle={() => toggleUnitExpansion(row.unitId)}
                                    selectedProviders={selectedProviders}
                                    setSelectedProviders={setSelectedProviders}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function SummaryCard({
    label,
    value,
    format,
}: {
    label: string;
    value: number | null;
    format: "percent" | "currency" | "days" | "number";
}) {
    return (
        <div className="rounded-sm border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
            <p className="mt-2 text-lg font-semibold text-black dark:text-white">
                {formatValue(value, format)}
            </p>
        </div>
    );
}
